import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Target, Heart, Award, Users } from "lucide-react";

export default function AboutPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-60" />
        <div aria-hidden className="pointer-events-none absolute -top-40 right-10 h-[440px] w-[440px] rounded-full bg-emerald/10 blur-[120px]" />
        <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-9">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> About MerchantCore
            </span>
            <h1 className="mt-7 font-display text-4xl font-bold leading-[1.02] tracking-tight text-cloud sm:text-6xl lg:text-7xl">
              Pioneering secure payment
              <br />
              solutions for <span className="text-gradient">high-risk</span> merchants.
            </h1>
          </div>
          <div className="lg:col-span-3">
            <p className="font-sans text-sm leading-relaxed text-muted">
              We build the global operating system for fintech — starting with
              payments. Founded 2008. Trusted by 12,500+ merchants worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="section pt-0" data-testid="about-story">
        <div className="container-wide grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> 01 — Our story
            </span>
            <h2 className="section-title mt-6">
              Rewriting financial services
              <br />
              <span className="text-gradient">one API at a time.</span>
            </h2>
          </div>
          <div className="space-y-6 lg:col-span-7">
            <p className="font-sans text-base leading-relaxed text-cloud/90 sm:text-lg">
              Since our founding in 2008, MerchantCore has been on a mission to
              create the most accessible financial services ecosystem in history.
              We are building the global operating system for fintech, starting
              with payments.
            </p>
            <p className="body-text">
              Moving money is a fundamental human activity and critical to the
              health of our global economy. Innovation from established financial
              providers has failed to keep pace with the needs of modern SaaS
              platforms and their users. MerchantCore exists to lower barriers to
              entry, drive competition, and make the financial system more
              equitable and accessible for people around the world.
            </p>
            <p className="body-text">
              Our team stays ahead of the curve in the ever-evolving payments
              landscape. By leveraging the latest advances in encryption, fraud
              detection, and risk management, we give clients peace of mind that
              their transactions are protected against potential threats.
            </p>

            <div className="grid grid-cols-2 gap-4 pt-4 sm:grid-cols-4">
              <Metric label="Monthly volume" value="$1.2B+" />
              <Metric label="Uptime" value="99.99%" />
              <Metric label="Acquirers" value="20+" />
              <Metric label="Currencies" value="150+" />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section pt-0" data-testid="about-values">
        <div className="container-wide">
          <div className="lg:max-w-3xl">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> 02 — Values
            </span>
            <h2 className="section-mega mt-6">
              What drives us <span className="text-gradient">every day.</span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Target, title: "Mission-driven", body: "Every decision ties back to making payments more accessible for merchants everywhere.", n: "01" },
              { icon: ShieldCheck, title: "Security first", body: "PCI DSS Level 1 certified with bank-grade encryption and fraud protection.", n: "02" },
              { icon: Heart, title: "Customer obsessed", body: "24/7/365 dedicated support from real payments experts who know your industry.", n: "03" },
              { icon: Award, title: "Integrity", body: "Transparent pricing, honest advice, and relationships built to last.", n: "04" },
            ].map(({ icon: Icon, title, body, n }) => (
              <article key={n} data-testid={`value-card-${n}`} className="card card-hover group">
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-emerald/10 text-emerald">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-ultra text-faint">{n}</span>
                </div>
                <h3 className="mt-6 font-display text-lg font-semibold tracking-tight text-cloud">{title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BELIEFS */}
      <section className="section pt-0" data-testid="about-beliefs">
        <div className="container-wide grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> 03 — Beliefs
            </span>
            <h2 className="section-title mt-6">
              Every business deserves the
              <br />
              <span className="text-gradient">best</span> payment technology.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="font-sans text-base leading-relaxed text-cloud/90 sm:text-lg">
              We believe every business should have access to the best and most
              innovative payment technology — and that they should earn the bulk
              of the economic benefit. To accelerate access for the greater good,
              we remove the technical, compliance, regulatory, and global
              barriers to entry.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Instant merchant onboarding with full KYC handled in-house",
                "Acquirer-agnostic gateway — multiple MIDs under one integration",
                "Weekly card-scheme rule updates baked into our compliance team",
                "Chargeback defender — early warnings turn disputes into refunds",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-4 rounded-xl border border-line bg-white/[0.02] px-4 py-3.5">
                  <span className="font-mono text-[11px] uppercase tracking-ultra text-emerald">0{i + 1}</span>
                  <span className="font-sans text-sm leading-relaxed text-cloud/90">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24" data-testid="about-cta">
        <div className="container-wide">
          <div className="relative overflow-hidden rounded-3xl border border-emerald/30 bg-surface p-10 shadow-glow sm:p-14">
            <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-50" />
            <div aria-hidden className="pointer-events-none absolute -bottom-24 right-1/4 h-[340px] w-[340px] rounded-full bg-emerald/15 blur-[110px]" />
            <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[1.6fr_auto]">
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4 text-emerald" />
                  <span className="font-mono text-[11px] uppercase tracking-ultra text-muted">Join our network</span>
                </div>
                <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight text-cloud sm:text-4xl lg:text-5xl">
                  Ready to build the future
                  <br />
                  of payments <span className="text-gradient">together?</span>
                </h2>
              </div>
              <Link to="/contact-us" className="btn-primary" data-testid="about-cta-button">
                Get in touch <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl border border-line bg-white/[0.02] p-5">
      <div className="font-mono text-2xl font-bold tracking-tight text-cloud sm:text-3xl">{value}</div>
      <div className="mt-2 font-mono text-[11px] uppercase tracking-ultra text-muted">{label}</div>
    </div>
  );
}
